import React from 'react';
import {
  Message,
  MessageContent,
  MarkdownRenderer,
} from '../ai-elements';
import type { AgentTeamMessageProps } from '../../adapters/agentTeamAdapter';
import type { AIElementsMessagePart } from '../../adapters/chatAdapter';
import type { ConversationRole } from '../../types/agentTeam';
import { ROLE_DISPLAY_MAP, ROLE_EMOJI_MAP } from '../../adapters/agentTeamAdapter';
import { Reasoning } from '../ai-elements/reasoning';
import { ToolCardRenderer } from '../ai-elements/tool-registry';

const ROLE_COLORS: Record<ConversationRole, { bg: string; text: string }> = {
  user: { bg: 'bg-blue-50', text: 'text-blue-900' },
  orchestrator: { bg: 'bg-amber-50', text: 'text-amber-900' },
  executor: { bg: 'bg-green-50', text: 'text-green-900' },
  system: { bg: 'bg-gray-50', text: 'text-gray-900' },
  planner: { bg: 'bg-purple-50', text: 'text-purple-900' },
};

const AgentAvatar: React.FC<{ role: ConversationRole; emoji?: string }> = ({ role, emoji }) => {
  const colors = ROLE_COLORS[role];
  return (
    <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs ${colors.bg}`}>
      <span className="text-sm">{emoji || ROLE_EMOJI_MAP[role]}</span>
    </div>
  );
};

const PartRenderer: React.FC<{ part: AIElementsMessagePart }> = ({ part }) => {
  if (part.type === 'reasoning') {
    return (
      <Reasoning isStreaming={!part.isDone} defaultOpen={!part.isDone}>
        <div className="whitespace-pre-wrap">{part.content || ''}</div>
      </Reasoning>
    );
  }
  if (part.type === 'tool') {
    if (part.toolName === 'ask_user') return null;
    return (
      <ToolCardRenderer
        toolCall={{
          toolCallId: part.toolCallId,
          toolName: part.toolName,
          status: part.status,
          args: part.args,
          result: part.result,
          error: part.error,
          timestamp: part.timestamp,
        }}
      />
    );
  }
  return <MarkdownRenderer content={part.content} />;
};

export const AgentTeamMessage: React.FC<{ message: AgentTeamMessageProps }> = ({ message }) => {
  const colors = ROLE_COLORS[message.agentRole || 'system'];
  const hasParts = message.parts && message.parts.length > 0;
  const hasNonTextParts = hasParts && message.parts!.some(p => p.type !== 'text' && !(p.type === 'tool' && p.toolName === 'ask_user'));
  const hasContent = !!(message.content) || (hasParts && message.parts!.some(p =>
    (p.type === 'text' && p.content) || p.type === 'reasoning' || (p.type === 'tool' && p.toolName !== 'ask_user')
  ));

  return (
    <Message
      role={message.role}
      avatar={<AgentAvatar role={message.agentRole || 'system'} emoji={message.roleEmoji} />}
      isLoading={message.isLoading && !hasContent}
    >
      <MessageContent>
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-medium ${colors.text}`}>
            {message.speakerName || ROLE_DISPLAY_MAP[message.agentRole || 'system']}
          </span>
          {message.isLoading && hasContent && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
              <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              回复中
            </span>
          )}
          <span className="text-xs text-gray-400 ml-auto">
            {new Date(message.createdAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div className={`${colors.text} text-sm leading-relaxed break-words min-w-0`}>
          {hasNonTextParts ? (
            <>
              {message.parts!.map((part, idx) => (
                <PartRenderer key={idx} part={part} />
              ))}
              {message.isLoading && (
                <span className="inline-block w-1.5 h-4 ml-1 bg-purple-500 animate-pulse" />
              )}
            </>
          ) : (
            <>
              {message.content && (
                <MarkdownRenderer content={message.content} />
              )}
              {message.isLoading && (
                <span className="inline-block w-1.5 h-4 ml-1 bg-purple-500 animate-pulse" />
              )}
            </>
          )}
        </div>
      </MessageContent>
    </Message>
  );
};

export default AgentTeamMessage;
